import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { WatchList } from '~/shared/data-contract/watch';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';
import { optimisticallyUnwatchItem, restoreWatches } from './watch-cache';

type WatchMutationContext = {
    readonly previous: WatchList;
};

export type UseUnwatchItemResult = UseMutationResult<unknown, Error, string, WatchMutationContext>;

export const useUnwatchItem = (): UseUnwatchItemResult => {
    const { unwatchItem } = useApplicationFetchers();
    const queryClient = useQueryClient();

    return useMutation<unknown, Error, string, WatchMutationContext>({
        mutationFn: async (itemId: string) => {
            const result = await unwatchItem(itemId);

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        onError: (_error, _itemId, context) => {
            if (context !== undefined && context !== null) {
                restoreWatches(queryClient, context.previous);
            }
        },
        onMutate: async (itemId) => {
            await queryClient.cancelQueries({ queryKey: jobArchitectureKeys.watches() });

            return { previous: optimisticallyUnwatchItem(queryClient, itemId) };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.watches() });
        },
        throwOnError: false,
    });
};
